import React from 'react'

import { BirthdaySelectionMode } from '@facades/birthday'

import {
  DurationButton,
  DurationContainer,
  ProgramContainer,
  ProgramHeader,
  ProgramStep,
  ProgramSteps,
} from './birthdayProgram.styled'

function addMinutes(time: string, minutes: number) {
  const [hours, mins] = time.split(':').map(Number)
  const total = hours * 60 + mins + minutes
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(
    total % 60
  ).padStart(2, '0')}`
}

type BirthdayProgramProps = {
  start: string
  duration: number
  selectionMode: BirthdaySelectionMode
  sessions: Array<{
    id: string
    roomName: string
    start: string
  }>
  onDurationChange: (duration: 150 | 210) => void
}

export const BirthdayProgram = ({
  start,
  duration,
  selectionMode,
  sessions,
  onDurationChange,
}: BirthdayProgramProps) => {
  const staggered = selectionMode === 'staggered'
  const sortedSessions = [...sessions].sort((left, right) =>
    left.start.localeCompare(right.start)
  )
  const lastStart = sortedSessions[sortedSessions.length - 1]?.start ?? start
  const steps = staggered
    ? [
        ...sortedSessions.map((session) => ({
          id: session.id,
          time: `${session.start}–${addMinutes(session.start, 90)}`,
          title: session.roomName,
          description: `Příchod skupiny v ${
            session.start
          }, začátek hry v ${addMinutes(session.start, 30)}.`,
        })),
        {
          id: 'celebration-finale',
          time: `${addMinutes(lastStart, 90)}–${addMinutes(lastStart, 150)}`,
          title: 'Společný závěr oslavy',
          description: 'Dort, dárky a společný čas po poslední hře.',
        },
      ]
    : [
        {
          id: 'arrival',
          time: `${start}–${addMinutes(start, 30)}`,
          title: 'Příchod a občerstvení',
          description: 'Setkání s kamarády a občerstvení před hrou.',
        },
        {
          id: 'games',
          time: `${addMinutes(start, 30)}–${addMinutes(start, 90)}`,
          title: 'Únikové hry',
          description: 'Všechny vybrané týmy hrají souběžně.',
        },
        {
          id: 'celebration',
          time: `${addMinutes(start, 90)}–${addMinutes(start, 150)}`,
          title: 'Dort a dárky',
          description: 'Čas na svíčky, gratulace a vlastní aktivity.',
        },
        {
          id: 'extension',
          time: `${addMinutes(start, 150)}–${addMinutes(start, 210)}`,
          title: 'Hodina navíc',
          description: 'Volitelné prodloužení společného času.',
        },
      ]

  return (
    <ProgramContainer>
      <ProgramHeader>
        <div>
          <h2>Program narozeninové oslavy</h2>
          <span>
            {staggered
              ? `Délka se počítá automaticky: ${String(duration / 60).replace(
                  '.',
                  ','
                )} hodiny.`
              : 'Hry začínají 30 minut po příchodu hostů.'}
          </span>
        </div>
        {!staggered && (
          <DurationContainer>
            <DurationButton
              selected={duration === 150}
              onClick={() => onDurationChange(150)}
            >
              2,5 hodiny
            </DurationButton>
            <DurationButton
              selected={duration === 210}
              onClick={() => onDurationChange(210)}
            >
              3,5 hodiny
            </DurationButton>
          </DurationContainer>
        )}
      </ProgramHeader>
      <ProgramSteps>
        {steps.map((step, index) => (
          <ProgramStep
            key={step.id}
            disabled={!staggered && index === 3 && duration === 150}
          >
            <strong>{step.time}</strong>
            <h3>{step.title}</h3>
            <p>{step.description}</p>
          </ProgramStep>
        ))}
      </ProgramSteps>
    </ProgramContainer>
  )
}
